<script setup lang="ts">
import EmojiPicker from '@/components/emoji/EmojiPicker.vue';
import { computed, onMounted, ref, type ComputedRef } from 'vue';
import { type EmojiData } from 'emoji-mart-vue-fast/src';
import { useStorage } from '@vueuse/core';
import { cn, PPopover, PPopoverContent, PPopoverTrigger } from '@pokerly/ui';
import { emojiIndex } from '@/lib/emoji.ts';
import { IconMoodPlus } from '@tabler/icons-vue';

const storedRecentEmojis = useStorage<Record<string, number>>('pokerly-recent-emojis', {
    '+1': 1000,
    '-1': 900,
    heart: 800,
    angry: 20,
    open_mouth: 15,
    sob: 10,
});

const emit = defineEmits<{ select: [emoji: EmojiData]; 'update:open': [value: boolean] }>();

function handleOpenChange(value: boolean) {
    emit('update:open', value);
}

function handleEmojiSelect(emoji: EmojiData, close?: () => void) {
    if (close) {
        // A new emoji was selected from the picker popover. Update recent emojis.
        close();
        if (!storedRecentEmojis.value[emoji.id]) {
            // Update local storage to keep only top 5 recent emojis as they are selected
            const topEmojis = Object.entries(storedRecentEmojis.value)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5);
            storedRecentEmojis.value = Object.fromEntries(topEmojis);

            // Also update the in-memory recent emojis to reflect this change immediately
            recentEmojis.value = Object.fromEntries(
                Object.entries(recentEmojis.value)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5),
            );
            recentEmojis.value[emoji.id] = (recentEmojis.value[emoji.id] || 0) + 1;
        }
    }
    // Only update local storage. The update list will be reflected in the UI on next mount.
    storedRecentEmojis.value[emoji.id] = (storedRecentEmojis.value[emoji.id] || 0) + 1;
    emit('select', emoji);
}

const recentEmojis = ref<Record<string, number>>({});
onMounted(() => {
    // Get a copy of the recent emojis to handle during the lifecycle of the component
    recentEmojis.value = { ...storedRecentEmojis.value };
});

const sortedRecentEmojis: ComputedRef<EmojiData[]> = computed(() =>
    Object.entries(recentEmojis.value)
        .sort((a, b) => b[1] - a[1])
        .map(([id]) => emojiIndex.emoji(id)),
);
const emojiButtonStyle = cn('rounded-full', 'size-8', 'transition-transform', 'duration-200', 'ease-out');
</script>

<template>
    <div class="bg-surface-backdrop inline-flex rounded-l-full rounded-r-full border border-border-border-5 p-2 gap-2">
        <div
            v-for="emoji in sortedRecentEmojis"
            :key="emoji.id"
            :class="cn(emojiButtonStyle, 'relative  hover:bg-surface-ghost-hover active:bg-surface-ghost-active')"
        >
            <button
                type="button"
                :class="
                    cn(
                        emojiButtonStyle,
                        'relative',
                        'text-[1.6rem]',
                        'leading-none',
                        'align-middle',
                        'hover:-rotate-15',
                        'hover:scale-125',
                        'active:rotate-0',
                        'active:scale-75',
                    )
                "
                @click="handleEmojiSelect(emoji)"
            >
                {{ emoji.native }}
            </button>
        </div>
        <PPopover v-slot="{ close }" @update:open="handleOpenChange">
            <PPopoverTrigger as-child>
                <div
                    :class="
                        cn(
                            emojiButtonStyle,
                            'relative ',
                            'hover:bg-surface-ghost-hover',
                            ' active:bg-surface-ghost-active',
                        )
                    "
                >
                    <button
                        type="button"
                        :class="
                            cn(emojiButtonStyle, 'text-foreground-on-primary', 'hover:scale-125', 'active:scale-75')
                        "
                    >
                        <IconMoodPlus class="w-full h-full" />
                    </button>
                </div>
            </PPopoverTrigger>
            <PPopoverContent class="p-0 bg-transparent border-none outline-none shadow-none">
                <EmojiPicker @select="(emoji: EmojiData) => handleEmojiSelect(emoji, close)" />
            </PPopoverContent>
        </PPopover>
    </div>
</template>
